//@flow
import {useEffect, useRef, useState, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {useLocation} from 'react-router-dom';

import {actions as chatActions} from '../duck';
import {useActions} from '../../../../utils/globalStore';
import {
  createMetaAccountService,
  getFbMessengerAccountsService,
  getInstagramAccountsService,
  removeDeviceService,
  removeDeviceWithoutHistoryService,
  reconnectDeviceService,
  getWhatsappApiTemplatesService,
  getWhatsappApiAccountsService,
} from '../promises';

import type {
  StoreDevice,
  MetaAccount,
  MetaUserResponse,
  ChatServiceType,
} from '../../../../entities';

import type {RootState} from '../../../';
import {getRedirectUri} from '../components/Configuration/SocialConfig/meta-config';
import {useCallback} from 'react';
import {useAlert} from '../../../../elements/Alert/AlertContext';

import type {WhatsAppApiAccount} from '../../../../entities/multichat/whatsapp-api';
import {formatPhoneNumber} from '../../../../utils/phone';

const ravenAccountSelector = (state: RootState) => ({
  ravenAccount: state.chat.ravenAccount,
  isLoading: state.chat.isFetchingRavenAccount,
});

const MAX_RETRIES = 5;

const REDIRECT_URI = getRedirectUri();

export function useRavenAccount() {
  const getRavenAccount = useActions(chatActions.getRavenAccount);
  const {ravenAccount, isLoading} = useSelector(ravenAccountSelector);

  const retryCounter = useRef(0);

  useEffect(() => {
    if (retryCounter.current >= MAX_RETRIES) return;
    if (ravenAccount || isLoading) return;
    retryCounter.current += 1;
    getRavenAccount();
  }, [getRavenAccount, isLoading, ravenAccount]);

  return {ravenAccount, isLoading};
}

export function useCreateMetaAccount(
  type: string
): {
  metaAccounts: MetaAccount[],
  metaUser: MetaUserResponse | null,
  whatsappApiAccounts: WhatsAppApiAccount[],
  loading: boolean,
} {
  const location = useLocation();

  const [metaAccounts, setMetaAccounts] = useState<MetaAccount[]>([]);
  const [metaUser, setMetaUser] = useState<MetaUserResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const [whatsappApiAccounts, setWhatsappApiAccounts] = useState<
    WhatsAppApiAccount[]
  >([]);

  const {showAlert} = useAlert();

  const query = new URLSearchParams(location.search);
  const metaAppMetaId = query.get('metaAppId');
  const code = query.get('code');
  const error = query.get('error');

  useEffect(() => {
    if (!error) return;
    showAlert('Oops! ocurrió un error al intentar conectar tu cuenta', {
      duration: 5000,
      onClose: () => {
        window.close();
      },
      type: 'error',
    });
  }, [error, showAlert]);

  useEffect(() => {
    if (error) return;
    if (!code || !metaAppMetaId) return;

    let service = 'FB_MESSENGER';

    if (type === 'fb') {
      service = 'FB_MESSENGER';
    }
    if (type === 'ig') {
      service = 'INSTAGRAM';
    }
    if (type === 'wb') {
      service = 'WHATSAPP_API';
    }

    setLoading(true);

    createMetaAccountService({
      service,
      code,
      metaAppMetaId,
      redirectUri: REDIRECT_URI,
    })
      .then(metaUser => {
        if (!metaUser.id) {
          if (type === 'wb') {
            getWhatsappApiAccountsService().then(res =>
              setWhatsappApiAccounts(res.accounts)
            );
            return;
          }
        }

        setMetaUser(metaUser);
        const {id} = metaUser;

        if (type === 'fb') {
          return getFbMessengerAccountsService(id).then(res =>
            setMetaAccounts(res.accounts)
          );
        }

        return getInstagramAccountsService(id).then(res =>
          setMetaAccounts(res.accounts)
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [code, error, metaAppMetaId, type]);

  return {
    metaAccounts,
    metaUser,
    whatsappApiAccounts,
    loading,
  };
}

const storeDevicesSelector = (state: RootState) => ({
  storeDevices: state.chat.storeDevices || [],
  isLoading: state.chat.isFetchingStoreDevices,
  ravenAccount: state.chat.ravenAccount,
});

export function useInitializeStoreDevices(): {
  storeDevices: StoreDevice[],
  isLoading: boolean,
} {
  const getStoreDevices = useActions(chatActions.getStoreDevices);
  const {storeDevices, isLoading, ravenAccount} = useSelector(
    storeDevicesSelector
  );

  const triesCounter = useRef(0);

  useEffect(() => {
    if (!ravenAccount || storeDevices.length > 0) return;
    if (triesCounter.current >= MAX_RETRIES) return;

    triesCounter.current += 1;

    getStoreDevices([
      'WHATSAPP_WEB',
      'FB_MESSENGER',
      'INSTAGRAM',
      'WHATSAPP_API',
    ]);
  }, [getStoreDevices, ravenAccount, storeDevices.length]);

  return {storeDevices, isLoading};
}

const storeDeviceActions = {
  deleteStoreDevice: chatActions.deleteStoreDevice,
  reconnectStoreDevice: chatActions.reconnectStoreDevice,
  getStoreDevices: chatActions.getStoreDevices,
};

type UseStoreDevicesParams = {
  refetchOnMount?: boolean,
};
export function useStoreDevices({refetchOnMount}: UseStoreDevicesParams = {}) {
  const {storeDevices, isLoading} = useInitializeStoreDevices();
  const {deleteStoreDevice, getStoreDevices, reconnectStoreDevice} = useActions(
    storeDeviceActions
  );

  const storeDevicesNotArchived: StoreDevice[] = useMemo(() => {
    return [...storeDevices]
      .filter(device => device.state !== 'ARCHIVED')
      .reverse();
  }, [storeDevices]);

  const manualRefresh = useCallback(() => {
    getStoreDevices([
      'WHATSAPP_WEB',
      'FB_MESSENGER',
      'INSTAGRAM',
      'WHATSAPP_API',
    ]);
  }, [getStoreDevices]);

  useEffect(() => {
    if (!refetchOnMount) return;
    manualRefresh();
  }, [manualRefresh, refetchOnMount]);

  const deleteDeviceWithHistory = useCallback(
    (deviceId: string, serviceType: ChatServiceType) => {
      removeDeviceService(deviceId, serviceType).then(() => {
        deleteStoreDevice(deviceId, serviceType);
      });
    },
    [deleteStoreDevice]
  );

  const deleteDeviceWithoutHistory = (
    deviceId: string,
    serviceType: ChatServiceType
  ) => {
    removeDeviceWithoutHistoryService(deviceId, serviceType).then(() => {
      deleteStoreDevice(deviceId, serviceType);
    });
  };

  const reconnectDevice = (deviceId: string, serviceType: ChatServiceType) => {
    reconnectDeviceService(deviceId, serviceType).then(response => {
      reconnectStoreDevice(deviceId, serviceType, response?.qrCode?.qrCode);
    });
  };

  return {
    storeDevices,
    storeDevicesNotArchived,
    isLoading,
    deleteDeviceWithHistory,
    deleteDeviceWithoutHistory,
    reconnectDevice,
    manualRefresh,
  };
}

const deviceReconnectSelector = (state: RootState) => ({
  deviceReconnect: state.chat.deviceReconnect,
});

export function useDeviceReconnect() {
  const {deviceReconnect} = useSelector(deviceReconnectSelector);
  const resetReconnectActions = useActions(
    chatActions.resetReconnectStoreDevice
  );

  const resetReconnect = useCallback(() => {
    resetReconnectActions();
  }, [resetReconnectActions]);

  return {
    qrCodeReconnect: deviceReconnect.qrCode,
    phoneReconnect: deviceReconnect.phone,
    resetReconnect,
  };
}

export function useWhatsappApiTemplates(deviceId?: ?string) {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const {showAlert} = useAlert();

  const {isLoading: ravenAccountLoading} = useRavenAccount();

  const {
    isLoading: storeDevicesLoading,
    storeDevices,
  } = useInitializeStoreDevices();

  const connectedWhatsappApiDevices = useMemo(
    () =>
      storeDevices.filter(
        device =>
          device.service === 'WHATSAPP_API' && device.state === 'CONNECTED'
      ),
    [storeDevices]
  );

  const resolvedDeviceId = useMemo(() => {
    if (deviceId) {
      const matched = connectedWhatsappApiDevices.find(
        device => device.id === deviceId
      );
      if (matched) return matched.id;
    }

    // Auto-select only when there is exactly one CONNECTED WA API device
    if (connectedWhatsappApiDevices.length === 1) {
      return connectedWhatsappApiDevices[0].id;
    }

    return undefined;
  }, [connectedWhatsappApiDevices, deviceId]);

  useEffect(() => {
    if (!resolvedDeviceId) {
      setTemplates([]);
      return;
    }
    setIsLoading(true);
    getWhatsappApiTemplatesService(resolvedDeviceId)
      .then(response => {
        const templates = response.data;
        if (Array.isArray(templates)) {
          setTemplates(templates);
        } else {
          showAlert('No se encontraron templates', {
            type: 'warning',
          });
        }
      })
      .catch(error => {
        showAlert('Ocurrió un error al obtener los templates', {
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [showAlert, resolvedDeviceId]);

  return {
    templates,
    isLoading: isLoading || ravenAccountLoading || storeDevicesLoading,
  };
}

export function getServiceLabel(service: ChatServiceType) {
  if (service === 'INSTAGRAM') {
    return 'Instagram';
  }

  if (service === 'WHATSAPP_WEB') {
    return 'WhatsApp Web';
  }

  if (service === 'WHATSAPP_API') {
    return 'WhatsApp Api';
  }

  if (service === 'FB_MESSENGER') {
    return 'Messenger';
  }

  return 'Desconocido';
}

export function formatStoreDeviceIdentifier(device: {
  service: ChatServiceType,
  identifier: string,
}): string {
  if (device.service === 'WHATSAPP_WEB' || device.service === 'WHATSAPP_API') {
    if (device.identifier.startsWith('+')) {
      return `Número: ${formatPhoneNumber(device.identifier)}`;
    }
    return `Número: ${formatPhoneNumber(`+${device.identifier}`)}`;
  }

  if (device.service === 'FB_MESSENGER' || device.service === 'INSTAGRAM') {
    return `Cuenta: ${device.identifier}`;
  }

  return 'Desconocido';
}
